import { ArrowLeftRightIcon } from 'lucide-react';

import { SkuType } from '@invenco/products-interface';

import { Badge } from '@/components/core/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { cn } from '@/shared/helpers';
import { skuTypeIcon, skuTypeTitle } from '@/shared/title-maps';

type SkuBadgeProps = {
  name: string;
  type?: SkuType;
  externalSkuName?: string | null;
  reserveTypeIconSpace?: boolean;
  className?: string;
};

export function SkuBadge({
  name,
  type,
  externalSkuName,
  reserveTypeIconSpace,
  className,
}: SkuBadgeProps) {
  const TypeIcon = type && type !== SkuType.STANDARD ? skuTypeIcon[type] : undefined;
  const typeTitle = type ? skuTypeTitle[type] : undefined;
  const typeIcon = TypeIcon ? (
    <Badge className="size-5 px-0" title={typeTitle} variant="default">
      <TypeIcon
        aria-hidden
        // needed as the default of 2 is broken with the grid icon
        strokeWidth={2.5}
      />
    </Badge>
  ) : reserveTypeIconSpace ? (
    <span aria-hidden className="size-5 shrink-0" />
  ) : null;

  const nameContent = <span className="min-w-0 truncate">{name}</span>;

  const nameBadge = externalSkuName ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="max-w-full min-w-0 shrink" tabIndex={0}>
          <ArrowLeftRightIcon aria-hidden />
          {nameContent}
        </Badge>
      </TooltipTrigger>
      <TooltipContent aria-label={`${externalSkuName} mapped to ${name}`}>
        <span>{externalSkuName}</span>
        <ArrowLeftRightIcon className="size-3 text-muted-foreground" aria-hidden />
        <span>{name}</span>
      </TooltipContent>
    </Tooltip>
  ) : (
    <Badge className="max-w-full min-w-0 shrink">{nameContent}</Badge>
  );

  return (
    <span
      className={cn('inline-flex min-w-0 shrink-0 items-center gap-1.5 align-middle', className)}
    >
      {typeIcon}
      {nameBadge}
    </span>
  );
}
